/**
 * Product Form with Bulletproof Reservation System
 *
 * This component integrates with reserve-cart.js to ensure products can only be
 * added to one cart at a time. The flow is:
 *
 * 1. User clicks "Add to Cart"
 * 2. Pre-flight check: Call reserveProduct() to reserve in backend
 * 3. If reservation fails (product already reserved), show error and disable button
 * 4. If reservation succeeds, proceed to add to Shopify cart
 * 5. If Shopify cart addition fails, rollback the reservation
 *
 * Dependencies:
 * - reserve-cart.js must be loaded before this file
 * - Functions used: reserveProduct(), deleteFromCart(), getProductButtons(), disableAddToCartFromCollectionPage()
 * - Product buttons must have data-product-id attribute
 *
 * Error handling:
 * - All reservation failures prevent Shopify cart addition
 * - All Shopify failures trigger reservation rollback
 * - User sees clear error messages for each failure type
 */

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');

        if (ProductForm.hasDrawer()) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // perf: look the cart element up once per page (not once per card: 248 cards on big collections)
      static cartElement() {
        if (ProductForm._cart === undefined) ProductForm._cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        return ProductForm._cart;
      }

      static hasDrawer() {
        if (ProductForm._drawer === undefined) ProductForm._drawer = !!document.querySelector('cart-drawer');
        return ProductForm._drawer;
      }

      get cart() {
        return ProductForm.cartElement();
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();

        // Check if button is already disabled or aria-disabled
        if (this.submitButton.getAttribute('aria-disabled') === 'true' ||
            this.submitButton.hasAttribute('disabled')) {
          return;
        }

        this.handleErrorMessage();

        // CRITICAL: Disable button IMMEDIATELY to prevent double-clicks/race conditions
        this.submitButton.setAttribute('disabled', true);
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        // Get the product ID from the `product-id` attribute
        const productId = this.submitButton.dataset.productId;

        // CRITICAL: Reserve product in backend BEFORE adding to Shopify cart
        if (productId && typeof reserveProduct === 'function') {
          try {
            const reservationResult = await reserveProduct(productId);

            if (!reservationResult.success) {
              // Reservation failed - do not proceed to Shopify cart
              const errorMsg = reservationResult.error || 'This item is already in another customer\'s cart';
              this.handleErrorMessage(errorMsg);
              this.submitButton.classList.remove('loading');
              this.querySelector('.loading__spinner').classList.add('hidden');

              // Keep disabled and update to show it's in another cart
              const buttons = typeof getProductButtons === 'function'
                ? getProductButtons(productId)
                : [this.submitButton];

              buttons.forEach(btn => {
                if (btn) {
                  btn.setAttribute('disabled', true);
                  btn.setAttribute('aria-disabled', true);
                  const span = btn.querySelector('span');
                  if (span) {
                    span.textContent = 'in another cart';
                  } else {
                    btn.textContent = 'in another cart';
                  }
                }
              });

              // Trigger sync to update all UI elements
              if (typeof disableAddToCartFromCollectionPage === 'function') {
                await disableAddToCartFromCollectionPage();
              }

              return; // Stop execution - do not add to Shopify cart
            }

            console.log('Product reserved successfully, proceeding to Shopify cart');
          } catch (error) {
            console.error('Reservation error:', error);
            this.handleErrorMessage('Failed to reserve product. Please try again.');
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('disabled');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            return;
          }
        }

        // Proceed with Shopify cart addition only if reservation succeeded
        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                // Shopify cart addition failed - rollback reservation
                if (productId && typeof deleteFromCart === 'function') {
                  deleteFromCart(productId).catch(err => {
                    console.error('Failed to rollback reservation:', err);
                  });
                }

                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
                this.handleErrorMessage(response.description);

                const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                if (!soldOutMessage) return;
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButton.querySelector('span').classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
                return;
            } else if (!this.cart) {
                window.location = window.routes.cart_url;
                return;
            }
  
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;

            // SUCCESS: Product added to cart - disable button permanently
            if (productId) {
              // Mark button as successfully added so finally block doesn't re-enable
              this.submitButton.dataset.addedToCart = 'true';

              const buttons = typeof getProductButtons === 'function'
                ? getProductButtons(productId)
                : [this.submitButton];

              buttons.forEach(btn => {
                if (btn) {
                  btn.setAttribute('disabled', true);
                  btn.setAttribute('aria-disabled', true);
                  btn.dataset.addedToCart = 'true';
                  const span = btn.querySelector('span');
                  if (span) {
                    span.textContent = 'in your cart';
                  } else {
                    btn.textContent = 'in your cart';
                  }
                }
              });

              // Update all UI elements on the page
              if (typeof disableAddToCartFromCollectionPage === 'function') {
                disableAddToCartFromCollectionPage();
              }
            }

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error('Shopify cart error:', e);

            // Rollback reservation on Shopify cart failure
            if (productId && typeof deleteFromCart === 'function') {
              deleteFromCart(productId).catch(err => {
                console.error('Failed to rollback reservation:', err);
              });
            }

            this.handleErrorMessage('Failed to add to cart. Please try again.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

            // Only re-enable if NOT successfully added to cart
            if (!this.error && !this.submitButton.dataset.addedToCart) {
              this.submitButton.removeAttribute('aria-disabled');
            }

            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
